import { useCallback, useEffect, useState } from 'react';

import {
  listBooks,
  reportsBookEngagement,
  reportsFunnel,
  reportsRetention,
  type BookEngagement,
  type BookListItem,
  type FunnelResponse,
  type RetentionResponse,
} from '../../lib/panelApi';

type Props = {
  accessToken: string;
};

/**
 * Advanced analytics view: weekly retention heatmap, activation
 * funnel, and per-book drop-off histogram.
 *
 * Each section is self-contained — they share an accessToken and
 * nothing else — so future additions (LTV, churn, …) can drop in
 * without re-architecting the page.
 */
export function ReportsPage({ accessToken }: Props) {
  return (
    <div className="grid" style={{ gap: 14 }}>
      <RetentionCard accessToken={accessToken} />
      <FunnelCard accessToken={accessToken} />
      <BookEngagementCard accessToken={accessToken} />
    </div>
  );
}

// ─── Retention ─────────────────────────────────────────────────────

function RetentionCard({ accessToken }: { accessToken: string }) {
  const [weeks, setWeeks] = useState(8);
  const [data, setData] = useState<RetentionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportsRetention(accessToken, weeks);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setLoading(false);
    }
  }, [accessToken, weeks]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="card">
      <div className="pageHeader" style={{ marginBottom: 10 }}>
        <div>
          <div className="pageTitle">Weekly retention</div>
          <div className="pageSubtitle">
            % of each sign-up cohort still active in each following week. Darker means stickier.
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
            {[4, 6, 8, 10, 12].map((w) => (
              <option key={w} value={w}>{w} weeks</option>
            ))}
          </select>
          <button className="btn" onClick={refresh} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error ? <div className="errorBox">{error}</div> : null}

      {!data || data.cohorts.length === 0 ? (
        <div className="muted">No cohort data yet.</div>
      ) : (
        <RetentionHeatmap data={data} />
      )}
    </div>
  );
}

function RetentionHeatmap({ data }: { data: RetentionResponse }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table" style={{ tableLayout: 'fixed', minWidth: 100 + data.weeks * 64 }}>
        <thead>
          <tr>
            <th style={{ width: 110, textAlign: 'left' }}>Cohort</th>
            <th style={{ width: 60, textAlign: 'right' }}>Size</th>
            {Array.from({ length: data.weeks }, (_, i) => (
              <th key={i} style={{ width: 64, textAlign: 'center' }}>W{i}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.cohorts.map((c) => (
            <tr key={c.cohort_week}>
              <td style={{ fontWeight: 800 }}>{c.cohort_week}</td>
              <td style={{ textAlign: 'right' }} className="muted">{c.size}</td>
              {c.retention.map((r, i) => (
                <td key={i} style={{ textAlign: 'center' }}>
                  <RetentionCell value={r} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RetentionCell({ value }: { value: number }) {
  // 0.0–1.0 → purple ramp. Empty cells stay white to make sparse
  // recent cohorts (right edge of the heatmap) easy to read.
  if (value <= 0) {
    return <span className="muted" style={{ fontSize: 11 }}>—</span>;
  }
  const opacity = Math.min(0.95, 0.18 + value * 0.85);
  const pct = Math.round(value * 100);
  return (
    <span
      style={{
        display: 'inline-block',
        minWidth: 44,
        padding: '4px 6px',
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 800,
        background: `rgba(10,25,47,${opacity})`,
        color: opacity > 0.5 ? '#fff' : 'var(--text, #0A192F)',
      }}
    >
      {pct}%
    </span>
  );
}

// ─── Funnel ────────────────────────────────────────────────────────

function FunnelCard({ accessToken }: { accessToken: string }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<FunnelResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsFunnel(accessToken, days);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, days]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const total = data?.steps[0]?.count ?? 0;

  return (
    <div className="card">
      <div className="pageHeader" style={{ marginBottom: 10 }}>
        <div>
          <div className="pageTitle">Activation funnel</div>
          <div className="pageSubtitle">
            What fraction of new sign-ups completes each milestone.
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[7, 14, 30, 60, 90].map((d) => (
              <option key={d} value={d}>last {d} days</option>
            ))}
          </select>
          <button className="btn" onClick={refresh} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      {!data || data.steps.length === 0 ? (
        <div className="muted">No funnel data yet.</div>
      ) : (
        <div className="list">
          {data.steps.map((s, i) => {
            const pct = total > 0 ? (s.count / total) * 100 : 0;
            const prev = i > 0 ? data.steps[i - 1].count : null;
            const stepPct = prev && prev > 0 ? (s.count / prev) * 100 : null;
            return (
              <div className="item" key={s.key} style={{ paddingTop: 10, paddingBottom: 10 }}>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <div style={{ fontWeight: 850 }}>{i + 1}. {s.label}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {s.count} ({pct.toFixed(1)}%
                    {stepPct != null ? <> · {stepPct.toFixed(1)}% from previous</> : null})
                  </div>
                </div>
                <div style={{ height: 8, background: 'rgba(0,0,0,0.06)', borderRadius: 6, overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${pct}%`,
                      height: '100%',
                      background: 'var(--accent, #0a192f)',
                      transition: 'width 240ms ease',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Book engagement / drop-off ────────────────────────────────────

function BookEngagementCard({ accessToken }: { accessToken: string }) {
  const [books, setBooks] = useState<BookListItem[]>([]);
  const [bookUUID, setBookUUID] = useState<string>('');
  const [data, setData] = useState<BookEngagement | null>(null);
  const [loading, setLoading] = useState(false);

  // Hydrate the book picker once.
  useEffect(() => {
    let cancelled = false;
    listBooks(accessToken, 200, 0)
      .then((res) => {
        if (cancelled) return;
        setBooks(res.items);
        if (res.items.length > 0) setBookUUID(res.items[0].uuid);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  useEffect(() => {
    if (!bookUUID) return;
    let cancelled = false;
    setLoading(true);
    reportsBookEngagement(accessToken, bookUUID)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, bookUUID]);

  const maxBucket = data ? Math.max(1, ...data.dropoff_buckets.map((b) => b.count)) : 1;

  return (
    <div className="card">
      <div className="pageHeader" style={{ marginBottom: 10 }}>
        <div>
          <div className="pageTitle">Book engagement</div>
          <div className="pageSubtitle">
            Where listeners stop. Right-most bucket counts both 90–100% progress and books marked finished.
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <select value={bookUUID} onChange={(e) => setBookUUID(e.target.value)} style={{ maxWidth: 320 }}>
            {books.map((b) => (
              <option key={b.uuid} value={b.uuid}>
                {b.title || 'Untitled'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="muted">Loading…</div>
      ) : !data ? (
        <div className="muted">No data.</div>
      ) : (
        <>
          <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <Stat label="Listeners" value={data.listeners} />
            <Stat label="Finished"  value={data.finished} />
            <Stat label="Avg progress" value={`${Math.round(data.avg_progress_pct * 100)}%`} />
          </div>

          <div className="row" style={{ alignItems: 'flex-end', gap: 4, minHeight: 180 }}>
            {data.dropoff_buckets.map((b) => {
              const h = (b.count / maxBucket) * 140;
              return (
                <div key={b.bucket_start} style={{ flex: 1, textAlign: 'center' }}>
                  <div
                    title={`${Math.round(b.bucket_start * 100)}–${Math.round(b.bucket_end * 100)}%: ${b.count}`}
                    style={{
                      height: Math.max(2, h),
                      background:
                        b.bucket_start >= 0.9
                          ? 'var(--accent2, #00C85A)'
                          : 'var(--accent, #0a192f)',
                      borderRadius: '6px 6px 0 0',
                      transition: 'height 240ms ease',
                    }}
                  />
                  <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                    {Math.round(b.bucket_start * 100)}%
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>{b.count}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="pill" style={{ minWidth: 110, justifyContent: 'center', flexDirection: 'column', padding: '6px 12px' }}>
      <span className="muted" style={{ fontSize: 11 }}>{label}</span>
      <span style={{ fontWeight: 900, fontSize: 18 }}>{value}</span>
    </div>
  );
}
